import { CheckCircle2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import type { MissionDetail } from "@/types/mission-detail"

/**
 * OKS-390 — Requirements bullet list for the mission-detail page.
 *
 * There is no dedicated "requirements" column on the mission — the list is
 * composed FE-side from existing mission fields so every line stays
 * translatable (no server-rendered copy):
 *   - content format (+ free-text "other")
 *   - required hashtags (joined)
 *   - required mentions (joined)
 *   - product info
 *   - deadline
 *
 * Each present field becomes one `CheckCircle2` bullet. When NO field yields a
 * requirement, the whole section is omitted by the caller (this component
 * returns null) per UX Spec §3 (empty state).
 */
export interface RequirementsListProps {
  missionDetail: MissionDetail
  /** Pre-formatted deadline string (locale-aware), or null. */
  deadlineDisplay: string | null
}

export function RequirementsList({
  missionDetail,
  deadlineDisplay,
}: RequirementsListProps) {
  const { t } = useTranslation()
  const items = buildItems(t, missionDetail, deadlineDisplay)

  if (items.length === 0) {
    return null
  }

  return (
    <section
      data-testid="mission-detail-requirements"
      className="flex flex-col gap-2"
    >
      <h2 className="text-sm font-semibold">
        {t("creator_mission_detail.requirements.title")}
      </h2>
      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li
            key={item.key}
            className="flex items-start gap-2 text-sm"
            data-testid={`mission-detail-requirement-${item.key}`}
          >
            <CheckCircle2
              className="text-primary mt-0.5 size-4 shrink-0"
              aria-hidden="true"
            />
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

interface RequirementItem {
  key: string
  label: string
}

function buildItems(
  t: (key: string, opts?: Record<string, unknown>) => string,
  m: MissionDetail,
  deadlineDisplay: string | null,
): RequirementItem[] {
  const items: RequirementItem[] = []

  if (m.content_format) {
    const formatLabel =
      m.content_format === "other" && m.content_format_other
        ? m.content_format_other
        : t(`missions.formats.${m.content_format}`)
    items.push({
      key: "content_format",
      label: t("creator_mission_detail.requirements.content_format", {
        format: formatLabel,
      }),
    })
  }

  if (m.hashtags.length > 0) {
    items.push({
      key: "hashtags",
      label: t("creator_mission_detail.requirements.hashtags", {
        tags: m.hashtags.map((h) => `#${h}`).join(" "),
      }),
    })
  }

  if (m.mentions.length > 0) {
    items.push({
      key: "mentions",
      label: t("creator_mission_detail.requirements.mentions", {
        mentions: m.mentions.map((mention) => `@${mention}`).join(" "),
      }),
    })
  }

  if (m.product_info) {
    items.push({
      key: "product_info",
      label: t("creator_mission_detail.requirements.product_info", {
        info: m.product_info,
      }),
    })
  }

  if (deadlineDisplay) {
    items.push({
      key: "deadline",
      label: t("creator_mission_detail.requirements.deadline", {
        date: deadlineDisplay,
      }),
    })
  }

  return items
}
